import { skipToken } from '@reduxjs/toolkit/query';
import { BenefitTypes } from '@strata/constants';
import { formatDate } from '@strata/helper-functions';
import { FormSectionWithRows } from '@strata/react-form-section';
import { DetailLabelList, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { type ReactNode } from 'react';

import { NoAuthorizationNeededMessage } from 'components/shared/NoAuthorizationNeededMessage';
import { formatAuths } from 'services/referralHelpers';
import { useGetShippingInfoQuery } from 'store/api/onboardingApi';
import { useGetNeedsByDateQuery } from 'store/api/performanceApi';
import { type ReferralDetails as ReferralDetailsInterface } from 'store/api/treatmentApi';

interface ReferralDetailsProps {
    showNeedsByDate: boolean;
    selectedReferral?: {
        referralExpiration: string;
    };
    referralDetails?: ReferralDetailsInterface;
}

interface GenericDetailLabelValueProps {
    label: string;
    value?: ReactNode;
    values?: ReactNode[];
}

export const ReferralDetails = ({
    showNeedsByDate,
    selectedReferral = undefined,
    referralDetails = undefined,
}: ReferralDetailsProps) => {
    const { data: needsByDate } = useGetNeedsByDateQuery(
        showNeedsByDate &&
            referralDetails?.treatmentId &&
            referralDetails.benefit === BenefitTypes.PHARMACY
            ? { treatmentId: referralDetails.treatmentId }
            : skipToken,
    );

    const { data: shippingInfo } = useGetShippingInfoQuery(
        referralDetails?.referralId
            ? {
                  referralId: referralDetails.referralId,
              }
            : skipToken,
    );

    const detailLabelValuePairs = [
        {
            label: 'Outreach Start Date',
            value: formatDate(referralDetails?.outreachDate),
        },
        {
            label: 'Referral Expiration',
            value: formatDate(selectedReferral?.referralExpiration),
        },
        ...(showNeedsByDate
            ? [
                  {
                      label: 'Next Fill Needs By Date',
                      value: formatDate(needsByDate?.needsByDate),
                  },
              ]
            : []),
        {
            label: 'Auth Period(s) (Auth ID)',
            values:
                (referralDetails?.auths ?? []).length > 0
                    ? formatAuths(referralDetails!.auths, true)
                    : [<NoAuthorizationNeededMessage key="no-authorization-needed" />],
        },
        {
            label: 'Last Tracking Number',
            value: shippingInfo?.tracking_number,
        },
        {
            label: 'Shipment Date',
            value: formatDate(shippingInfo?.shipment_date),
        },
        {
            label: 'Shipment Carrier',
            value: shippingInfo?.shipment_carrier,
        },
    ] as GenericDetailLabelValueProps[];
    const pairsToShow = detailLabelValuePairs.map(({ label, value, values }) =>
        value ? (
            <DetailLabelValuePair
                key={label}
                style={{ width: '50%' }}
                label={label}
                value={value as React.ReactNode}
            />
        ) : (
            <DetailLabelList key={label} style={{ width: '50%' }} label={label} values={values} />
        ),
    );
    const getTwoPairs = (row: number) => {
        const zeroBasedIndex = row - 1;
        const rowStartIndex = zeroBasedIndex * 2;
        const rowEndIndex = row * 2;
        // If first index in row exists, then return that row
        if (pairsToShow[rowStartIndex]) {
            return pairsToShow.slice(rowStartIndex, rowEndIndex);
        }
        // Else, nothing to show for that row
        return null;
    };

    return (
        <FormSectionWithRows
            sectionTitle="Referral Details"
            width="50%"
            isLoading={!referralDetails}
            rowOneContent={getTwoPairs(1)}
            rowTwoContent={getTwoPairs(2)}
            rowThreeContent={getTwoPairs(3)}
            rowFourContent={getTwoPairs(4)}
            rowFiveContent={getTwoPairs(5)}
            rowSixContent={getTwoPairs(6)}
        />
    );
};
